import { Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import {
  ConfigurationService,
  DialogService,
  MarketLoyaltyService,
  SentryService,
  UserService
} from '@box-core/services';
import {
  ConfirmDialogData,
  MarketCard,
  MarketCardDisconnectOptions,
  APIError,
  SMLoyaltyConfiguration
} from '@box-types';
import { BoxConfirmDialogComponent } from '@box-shared/components';
import { generateMarketCard } from '@box/utils';
import { BehaviorSubject, catchError, Observable, throwError } from 'rxjs';
import { LanguageService } from '@box-core/services/language.service';
import { GlobalStateService } from '@box-core/services/global-state.service';

@Injectable()
export class AccountMarketCardsService {
  private readonly cardsSource = new BehaviorSubject<MarketCard[]>([]);
  public readonly cards$ = this.cardsSource.asObservable();

  constructor(
    private configService: ConfigurationService,
    private dialogService: DialogService,
    private sentryService: SentryService,
    private marketLoyaltyService: MarketLoyaltyService,
    private languageService: LanguageService,
    private globalStateService: GlobalStateService
  ) {}

  public setCards(cards: MarketCard[]): void {
    this.cardsSource.next(cards);
  }

  public getCards(): MarketCard[] {
    return this.cardsSource.getValue();
  }

  public clearCards(): void {
    this.cardsSource.next([]);
  }

  public removeCard(marketCard: MarketCard): void {
    const currentCards = this.cardsSource.getValue();
    this.cardsSource.next(currentCards.filter((card) => card.cardId !== marketCard.cardId));
  }

  public removeCardFromUser(marketCard: MarketCard): void {
    const user = this.globalStateService.getUser();
    const loyaltyCards = this.globalStateService.getSavedLoyaltyCards();
    const updatedLoyaltyCards = loyaltyCards.filter((card) => card.cardId !== marketCard.cardId);
    this.globalStateService.setUser({ ...user, loyaltyCards: updatedLoyaltyCards });
  }

  public disconnectCard(marketCard: MarketCard): Observable<void> {
    const { cardId, supermarket } = marketCard;
    const options: MarketCardDisconnectOptions = { cardId, company: supermarket };
    return this.marketLoyaltyService.disconnectCard(options).pipe(
      catchError((error: APIError) => {
        this.dialogService.openErrorDialog(error);
        this.sentryService.captureException(error, {
          domain: 'Supermarket Loyalty',
          domainDetails: 'Disconnect Market Card Endpoint',
          severity: 'warning'
        });
        return throwError(() => error);
      })
    );
  }

  public getUserCards(): MarketCard[] {
    const loyaltyCards = this.globalStateService.getUser()?.loyaltyCards;
    return loyaltyCards
      .map((clubCards) => {
        const cardInfo = this.getIntegratorOptions(clubCards.name);
        if (!cardInfo) return undefined;
        return generateMarketCard(clubCards, cardInfo);
      })
      .filter(Boolean);
  }

  public openDisconnectCardDialog(marketCard: MarketCard): MatDialogRef<BoxConfirmDialogComponent> {
    const translatedMessage = this.languageService
      .getTextByKey('reenter_it_the_next_time_you_wish_to_order_from_a_store')
      .replace('_SHOP_NAME', marketCard.shopName);
    const translatedTitle = this.languageService.getTextByKey('remove_card').replace('_CARD_NAME', marketCard.name);
    const config: ConfirmDialogData = {
      title: translatedTitle,
      messages: ['are_you_sure_you_want_to_remove_your_card', translatedMessage],
      confirmText: 'remove_',
      cancelText: 'cancel_'
    };
    return this.dialogService.openConfirmDialog(config);
  }

  private getIntegratorOptions(integrator: string): SMLoyaltyConfiguration {
    const boxConfig = this.configService.getConfiguration();
    if (!boxConfig) return undefined;
    const smLoyaltyInfo = boxConfig.smLoyaltyInfo ?? [];
    return smLoyaltyInfo.find((info) => info.integrator === integrator);
  }
}
